import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Loader2, ImageIcon, AlertCircle, Maximize2, X, Camera, SwitchCamera, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNetworkStatus } from '../../useNetworkStatus';
import { API_BASE_URL } from '../../utils/constants';
import { AIConsentModal } from '../modals';
import NativeBridge, { isNativeApp, canPurchaseDigitalGoods } from '../../native-bridge';

// Total stream timeout (fallback if server doesn't respond)
const STREAM_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes total

const VisionBoardGenerator = ({
  board,
  boardId,
  boards,
  setBoards,
  onSave,
  onBack,
  token,
  fetchUserStats,
  handleBuyCredits
}) => {
  const isNative = isNativeApp();
  const { t } = useTranslation();
  const { isOnline } = useNetworkStatus();
  const [step, setStep] = useState('input');
  const [currentVision, setCurrentVision] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]); // Array of { url, filename, expiresAt }
  const [genProgress, setGenProgress] = useState(null); // { current, total } while generating
  const [selectedForCanvas, setSelectedForCanvas] = useState([]); // Array of { url, filename, expiresAt }
  const [refFiles, setRefFiles] = useState([]);
  const [progressToast, setProgressToast] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const abortControllerRef = useRef(null);

  // AI consent state.
  //
  // On iOS the native shell owns the decision (guideline 5.1.2(i)) and pushes
  // it in, so this mirrors the native answer rather than deciding for itself.
  // On the web the in-page modal below is the gate.
  const [showAIConsent, setShowAIConsent] = useState(false);
  const [hasConsentedToAI, setHasConsentedToAI] = useState(() => NativeBridge.hasAIConsent());

  useEffect(() => {
    if (!isNativeApp()) {
      return undefined;
    }
    const handleConsentChange = (event) => {
      setHasConsentedToAI(event?.detail?.granted === true);
    };
    window.addEventListener('nativeAIConsentChanged', handleConsentChange);
    return () => window.removeEventListener('nativeAIConsentChanged', handleConsentChange);
  }, []);

  // Camera state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' for front camera, 'environment' for back
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleGenerateImages = async () => {
    if (!currentVision.trim()) return;

    // Never transmit a prompt before consent has been given.
    if (!hasConsentedToAI) {
      if (isNativeApp()) {
        // Defer to the native sheet so iOS has a single consent decision.
        NativeBridge.requestAIConsent();
      } else {
        setShowAIConsent(true);
      }
      return;
    }

    setIsGenerating(true);
    setProgressToast(null);
    setGeneratedImages([]); // Clear any previous images
    setSelectedForCanvas([]);
    setGenProgress({ current: 0, total: 4 });

    // Scroll to top so user can see progress toast on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Track images as they arrive for partial success handling
    const receivedImages = [];

    // Setup abort controller with timeout
    abortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => {
      console.warn('Stream timeout reached, aborting...');
      abortControllerRef.current?.abort();
    }, STREAM_TIMEOUT_MS);

    try {
      // Build multipart/form-data
      const form = new FormData();
      form.append('vision', currentVision);
      form.append('num_images', 4);
      for (let i = 0; i < refFiles.length; i++) {
        form.append('refs', refFiles[i]);
      }

      // Use EventSource alternative - fetch with streaming
      const response = await fetch(`${API_BASE_URL}/generate-images-stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: form,
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.quota_exceeded) {
          const quotaMessage = data.remaining !== undefined && data.needed !== undefined
            ? t('generator.quotaExceededMessage', { remaining: data.remaining, needed: data.needed })
            : t('generator.quotaExceededGeneric');
          setProgressToast({
            message: quotaMessage,
            status: 'error',
            // No "Buy Credits" prompt on iOS: credits are digital content and
            // would have to go through In-App Purchase (guideline 3.1.1).
            actionLabel: canPurchaseDigitalGoods() ? t('payment.buyCredits') : null,
            onAction: canPurchaseDigitalGoods() ? handleBuyCredits : null
          });
          setIsGenerating(false);
          setGenProgress(null);
          setTimeout(() => setProgressToast(null), 7000);
          return;
        }
        throw new Error(data.error || t('generator.failedToGenerate'));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (jsonStr.trim()) {
              try {
                const event = JSON.parse(jsonStr);

                if (event.type === 'start') {
                  setGenProgress({ current: 0, total: event.total });
                  setProgressToast(event.message);
                } else if (event.type === 'progress') {
                  setGenProgress({ current: event.current, total: event.total });
                  setProgressToast(event.message);
                } else if (event.type === 'image') {
                  // Track each image as it arrives
                  if (event.image) {
                    const newImage = {
                      url: event.image.url,
                      filename: event.image.filename,
                      expiresAt: event.image.expiresAt
                    };
                    receivedImages.push(newImage);
                    // Show each image as soon as it's ready instead of
                    // waiting for the whole batch to finish.
                    setGeneratedImages(prev => [...prev, newImage]);
                    setStep('select');
                  }
                  setGenProgress({ current: event.current, total: event.total });
                  setProgressToast(event.message);
                } else if (event.type === 'image_timeout' || event.type === 'image_failed') {
                  // Show warning but continue - server will keep going
                  setProgressToast({
                    message: event.message,
                    status: 'warning'
                  });
                } else if (event.type === 'complete') {
                  clearTimeout(timeoutId);
                  setGenProgress(null);
                  // Use images from completion event (most reliable)
                  const finalImages = event.images.map(img => ({
                    url: img.url,
                    filename: img.filename,
                    expiresAt: img.expiresAt
                  }));
                  setGeneratedImages(finalImages);

                  // Show appropriate message based on partial success
                  if (event.partial && finalImages.length > 0) {
                    setProgressToast({
                      message: event.message,
                      status: 'warning'
                    });
                  } else if (finalImages.length === 0) {
                    setProgressToast({
                      message: event.message,
                      status: 'error'
                    });
                  } else {
                    setProgressToast(event.message);
                  }

                  if (finalImages.length > 0) {
                    setStep('select');
                  }

                  // Update usage stats
                  if (event.usage) {
                    fetchUserStats();
                  }
                  // Clear toast after a moment
                  setTimeout(() => setProgressToast(null), 4000);
                } else if (event.type === 'error' || event.error) {
                  clearTimeout(timeoutId);
                  // Handle error - show toast and stop
                  let errorMsg = event.error || t('generator.failedToGenerate');
                  let actionLabel = null;
                  let onAction = null;

                  // Special handling for quota exceeded
                  if (errorMsg.toLowerCase().includes('quota exceeded')) {
                    if (event.remaining !== undefined && event.needed !== undefined) {
                      errorMsg = t('generator.quotaExceededMessage', { remaining: event.remaining, needed: event.needed });
                    } else {
                      errorMsg = t('generator.quotaExceededGeneric');
                    }
                    // Suppressed on iOS -- see the quota handler above.
                    if (canPurchaseDigitalGoods()) {
                      actionLabel = t('payment.buyCredits');
                      onAction = handleBuyCredits;
                    }
                  }

                  setProgressToast({
                    message: errorMsg,
                    status: 'error',
                    actionLabel,
                    onAction
                  });
                  setIsGenerating(false);
                  setGenProgress(null);

                  // Refresh user stats to show updated balance
                  fetchUserStats();

                  setTimeout(() => setProgressToast(null), 5000);
                  return; // Exit the function early
                }
              } catch (parseError) {
                console.error('Error parsing SSE event:', parseError);
                // Don't swallow errors - show them to the user
                setProgressToast({
                  message: t('generator.errorProcessingResponse'),
                  status: 'error'
                });
                setIsGenerating(false);
                setGenProgress(null);
                clearTimeout(timeoutId);
                setTimeout(() => setProgressToast(null), 5000);
                return;
              }
            }
          }
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort/timeout - show partial results if available
      if (error.name === 'AbortError') {
        console.warn('Stream was aborted (timeout or manual cancel)');
        if (receivedImages.length > 0) {
          setGeneratedImages(receivedImages);
          setStep('select');
          setProgressToast({
            message: t('generator.partialTimeout', { count: receivedImages.length }),
            status: 'warning'
          });
          fetchUserStats();
          setTimeout(() => setProgressToast(null), 5000);
          setIsGenerating(false);
          setGenProgress(null);
          return;
        }
      }

      console.error('Error generating images:', error);
      setProgressToast({
        message: t('generator.failedToGenerate'),
        status: 'error'
      });
      setTimeout(() => setProgressToast(null), 5000);
    }
    setIsGenerating(false);
    setGenProgress(null);
  };

  const handleRefFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setRefFiles(prev => [...prev, ...files]);
  };

  const handleRemoveRefFile = (indexToRemove) => {
    setRefFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      setCameraStream(stream);
      setShowCamera(true);
      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      if (isNativeApp()) {
        alert(t('generator.cameraPermissionError'));
      }
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const switchCamera = async () => {
    // Stop current stream
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    // Toggle facing mode
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    // Start with new facing mode
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error switching camera:', error);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob and create a File object
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setRefFiles(prev => [...prev, file]);
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  const handleAddToCanvas = async () => {
    const existingImages = board?.parsedData?.canvasImages || [];
    const existingVisions = board?.parsedData?.visions || [];

    // Store imageUrl (for display), filename (for persistence), and expiresAt (for cache management)
    const newImages = selectedForCanvas.map((img, idx) => ({
      id: `img_${Date.now()}_${idx}`,
      imageUrl: img.url,      // Current signed URL for display
      filename: img.filename, // Filename for generating new signed URLs later
      expiresAt: img.expiresAt, // Expiration timestamp for proactive refresh
      x: 100 + idx * 50,
      y: 100 + idx * 50,
      width: 200,
      height: 200,
      rotation: 0,
      zIndex: existingImages.length + idx,
    }));

    const updatedCanvasImages = [...existingImages, ...newImages];
    const updatedVisions = [...existingVisions, {
      id: `vision_${Date.now()}`,
      description: currentVision,
      generatedImages: selectedForCanvas.map(img => img.filename), // Store filenames only
    }];

    // Save the board with the new images
    const boardData = {
      id: boardId,
      title: board?.title || t('generator.defaultBoardTitle', { date: new Date().toLocaleDateString() }),
      data: JSON.stringify({
        visions: updatedVisions,
        canvasImages: updatedCanvasImages,
      })
    };

    if (token) {
      try {
        const response = await fetch(`${API_BASE_URL}/boards`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(boardData)
        });

        if (response.ok) {
          // Update local boards state
          if (setBoards) {
            setBoards({
              ...boards,
              [boardId]: {
                ...board,
                title: boardData.title,
                parsedData: {
                  visions: updatedVisions,
                  canvasImages: updatedCanvasImages,
                }
              }
            });
          }
          // Navigate to the full BoardView instead of simplified CanvasEditor
          if (onSave) onSave();
        }
      } catch (e) {
        console.error('Error saving board:', e);
      }
    }

    setCurrentVision('');
    setGeneratedImages([]);
    setSelectedForCanvas([]);
  };


  if (step === 'select') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4 sm:p-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">{t('generator.selectImagesTitle')}</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4">{t('generator.selectImagesDescription')}</p>

          {isGenerating && genProgress && (
            <div className="flex items-center gap-2 mb-4 text-sm sm:text-base font-medium text-purple-700">
              <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
              <span>{t('generator.generatingImageProgress', { current: Math.max(genProgress.current, generatedImages.length), total: genProgress.total })}</span>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {generatedImages.map((img, idx) => {
              const isSelected = selectedForCanvas.some(s => s.filename === img.filename);
              return (
                <div key={idx} className="relative group">
                  <div
                    className={`aspect-square rounded-xl overflow-hidden transition-all cursor-pointer ${
                      isSelected
                        ? 'ring-4 ring-purple-500'
                        : 'hover:ring-2 hover:ring-purple-300'
                    }`}
                  >
                    <img
                      src={img.url}
                      alt=""
                      className="w-full h-full object-cover"
                      onClick={() => setPreviewImage(img)}
                    />
                  </div>

                  {/* Mobile: Always visible select button */}
                  <button
                    onClick={() => {
                      if (isSelected) {
                        setSelectedForCanvas(selectedForCanvas.filter(s => s.filename !== img.filename));
                      } else {
                        setSelectedForCanvas([...selectedForCanvas, img]);
                      }
                    }}
                    className={`sm:hidden absolute bottom-2 left-2 right-2 py-2 rounded-lg text-xs font-medium ${
                      isSelected
                        ? 'bg-purple-500 text-white'
                        : 'bg-white/90 text-gray-800'
                    }`}
                  >
                    {isSelected ? `✓ ${t('common.selected')}` : t('common.select')}
                  </button>

                  {/* Desktop: Hover overlay controls */}
                  <div className="hidden sm:flex absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-xl items-center justify-center gap-2">
                    <button
                      onClick={() => setPreviewImage(img)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-2 bg-white text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-100 flex items-center gap-1"
                    >
                      <Maximize2 className="w-4 h-4" />
                      {t('common.view')}
                    </button>
                    <button
                      onClick={() => {
                        if (isSelected) {
                          setSelectedForCanvas(selectedForCanvas.filter(s => s.filename !== img.filename));
                        } else {
                          setSelectedForCanvas([...selectedForCanvas, img]);
                        }
                      }}
                      className={`opacity-0 group-hover:opacity-100 transition-opacity px-3 py-2 rounded-lg text-sm font-medium ${
                        isSelected
                          ? 'bg-purple-500 text-white hover:bg-purple-600'
                          : 'bg-white text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      {isSelected ? `✓ ${t('common.selected')}` : t('common.select')}
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Skeleton placeholders for images still being generated */}
            {isGenerating && genProgress && Array.from({
              length: Math.max(0, genProgress.total - generatedImages.length)
            }).map((_, idx) => (
              <div
                key={`skeleton-${idx}`}
                className="aspect-square rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 animate-pulse flex items-center justify-center"
              >
                <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400 animate-spin" />
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={() => setStep('input')}
              className="px-4 sm:px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm sm:text-base"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handleAddToCanvas}
              disabled={selectedForCanvas.length === 0}
              className="flex-1 px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {t('generator.addSelectedToCanvas', { count: selectedForCanvas.length })}
            </button>
          </div>
        </div>

        {/* Image Preview Modal */}
        {previewImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={() => setPreviewImage(null)}
          >
            <div className="relative w-full max-w-6xl max-h-full">
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute -top-10 sm:-top-12 right-0 text-white hover:text-gray-300 transition p-2"
              >
                <X className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
              <img
                src={previewImage.url}
                alt={t('generator.previewAlt')}
                className="w-full max-h-[80vh] sm:max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col sm:flex-row gap-2 sm:gap-3 w-full px-4 sm:px-0 sm:w-auto">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const isSelected = selectedForCanvas.some(s => s.filename === previewImage.filename);
                    if (isSelected) {
                      setSelectedForCanvas(selectedForCanvas.filter(s => s.filename !== previewImage.filename));
                    } else {
                      setSelectedForCanvas([...selectedForCanvas, previewImage]);
                    }
                  }}
                  className={`px-4 sm:px-6 py-3 rounded-lg font-medium transition text-sm sm:text-base ${
                    selectedForCanvas.some(s => s.filename === previewImage.filename)
                      ? 'bg-purple-500 text-white hover:bg-purple-600'
                      : 'bg-white text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  {selectedForCanvas.some(s => s.filename === previewImage.filename) ? `✓ ${t('common.selected')}` : t('generator.selectForCanvas')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewImage(null);
                  }}
                  className="px-4 sm:px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition text-sm sm:text-base"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4 sm:p-6">
      {/* AI Consent Modal */}
      {showAIConsent && (
        <AIConsentModal
          onConsent={() => {
            setHasConsentedToAI(true);
            setShowAIConsent(false);
            // Trigger generation after consent
            setTimeout(() => handleGenerateImages(), 100);
          }}
          onClose={() => setShowAIConsent(false)}
        />
      )}

      {progressToast && (
        <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 z-50 animate-slide-in">
          {(() => {
            const toast = typeof progressToast === 'string'
              ? { message: progressToast }
              : progressToast;
            const message = toast?.message || '';
            const isError = toast?.status === 'error'
              || message.toLowerCase().includes('failed')
              || message.toLowerCase().includes('error');
            return (
              <div className={`bg-white rounded-lg shadow-2xl p-3 sm:p-4 flex items-center gap-3 sm:min-w-[300px] ${
                isError
                  ? 'border border-red-200'
                  : 'border border-blue-200'
              }`}>
                <div className="flex-shrink-0">
                  {isError ? (
                    <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                  ) : (
                    <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 animate-spin" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-900">{message}</p>
                  {toast?.actionLabel && toast?.onAction && (
                    <button
                      type="button"
                      onClick={toast.onAction}
                      className="mt-1 text-xs sm:text-sm font-semibold text-purple-600 hover:text-purple-700 underline"
                    >
                      {toast.actionLabel}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-4 sm:p-8">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-3 sm:mb-4">
            <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('generator.describeVision')}</h2>
          <p className="text-sm sm:text-base text-gray-600">{t('generator.shareGoals')}</p>
          <p className="text-sm sm:text-base text-gray-600">{t('generator.oneVisionAtTime')}</p>
        </div>

        <textarea
          value={currentVision}
          onChange={(e) => setCurrentVision(e.target.value)}
          placeholder={t('generator.visionPlaceholder')}
          className={`w-full h-32 sm:h-40 px-3 sm:px-4 py-2 sm:py-3 ${
            isNative ? 'text-base' : 'text-sm sm:text-base'
          } border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none mb-4`}
        />

        <div className="mb-4 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900 mb-1 text-sm sm:text-base">{t('generator.referenceImagesRequired')}</p>
              <p className="text-xs sm:text-sm text-amber-800">{t('generator.referenceImagesDescription')}</p>
              <p className="text-xs sm:text-sm text-amber-800 hidden sm:block">{t('generator.referenceImagesDescriptionFull')}</p>
            </div>
          </div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{t('generator.yourReferencePhotos')}</label>

          {/* Upload and Camera buttons */}
          <div className="flex gap-2 mb-3">
            <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-amber-300 rounded-lg cursor-pointer hover:bg-amber-100 transition">
              <ImageIcon className="w-5 h-5 text-amber-700" />
              <span className="text-sm font-medium text-amber-800">{t('generator.uploadPhotos')}</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleRefFilesChange}
                className="hidden"
              />
            </label>
            <button
              type="button"
              onClick={startCamera}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-100 transition"
            >
              <Camera className="w-5 h-5 text-purple-700" />
              <span className="text-sm font-medium text-purple-800">{t('generator.takeSelfie')}</span>
            </button>
          </div>

          {/* Photo previews */}
          {refFiles.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {refFiles.map((f, idx) => (
                <div key={idx} className="relative w-16 h-16 rounded overflow-hidden bg-gray-100 border-2 border-amber-300">
                  <img src={URL.createObjectURL(f)} alt={t('generator.referenceImageAlt')} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => handleRemoveRefFile(idx)}
                    className="absolute top-0 right-0 bg-red-500 text-white w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Only warn once the user is actively trying to proceed (has typed a
              vision) — a red warning on first load reads as an error state */}
          {refFiles.length === 0 && currentVision.trim().length > 0 && (
            <p className="mt-2 text-sm text-red-600 font-medium">⚠️ {t('generator.noReferenceImages')}</p>
          )}
        </div>

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center p-4">
            <div className="relative w-full max-w-lg">
              {/* Close button */}
              <button
                onClick={stopCamera}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition p-2"
              >
                <X className="w-8 h-8" />
              </button>

              {/* Video preview */}
              <div className="relative bg-black rounded-xl overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-auto rounded-xl"
                  style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                />

                {/* Hidden canvas for capturing */}
                <canvas ref={canvasRef} className="hidden" />
              </div>

              {/* Camera controls */}
              <div className="flex items-center justify-center gap-6 mt-6">
                {/* Switch camera button */}
                <button
                  onClick={switchCamera}
                  className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 transition flex items-center justify-center"
                >
                  <SwitchCamera className="w-6 h-6 text-white" />
                </button>

                {/* Capture button */}
                <button
                  onClick={capturePhoto}
                  className="w-20 h-20 rounded-full bg-white hover:bg-gray-200 transition flex items-center justify-center border-4 border-purple-500"
                >
                  <div className="w-16 h-16 rounded-full bg-purple-500 hover:bg-purple-600 transition" />
                </button>

                {/* Placeholder for symmetry */}
                <div className="w-12 h-12" />
              </div>

              <p className="text-white text-center mt-4 text-sm">
                {t('generator.positionInFrame')}
              </p>
            </div>
          </div>
        )}

        <button
          onClick={handleGenerateImages}
          disabled={!currentVision.trim() || refFiles.length === 0 || isGenerating || !isOnline}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {!isOnline ? (
            <>
              <WifiOff className="w-5 h-5" />
              {t('offline.cannotGenerate')}
            </>
          ) : isGenerating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {genProgress && genProgress.current > 0
                ? t('generator.generatingImageProgress', { current: genProgress.current, total: genProgress.total })
                : t('generator.generatingImages')}
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              {t('generator.generateVisionImages')}
            </>
          )}
        </button>

        {onBack && (
          <button
            onClick={onBack}
            className="w-full mt-2 text-gray-600 py-2 text-sm hover:text-gray-800 transition"
          >
            {t('common.cancel')}
          </button>
        )}
      </div>
    </div>
  );
};

export default VisionBoardGenerator;
